import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITour, ITourInitialState } from "./tourModel";
import {
  ActiveInactiveTour,
  createTour,
  createTourAddons,
  deleteTour,
  deleteTourImage,
  deleteTourRate,
  duplicateTour,
  getAllActiveTours,
  getAllGroupTours,
  getAllTours,
  getImageByTourID,
  getTourAddonsByTourID,
  getTourByID,
  getTourCostWithAddonsByID,
  getTourDatesByTourID,
  getTourItineraryByID,
  getTourPlacesByTourID,
  getTourRatesByTourID,
  getUpcomingTourDatesByTourID,
  insertTourRate,
  TourImageUpload,
  updateTour,
  updateTourOtherInfo,
  updateTourRate,
} from "./services/tour.services";

const initialState: ITourInitialState = {
  loading: false,
  AddonsLoading: false,
  error: "",
  TourList: [],
  GroupToursList: [],
  TourCostList: [],
  TourID: null,
  TourImageID: null,
  TourCostID: null,
  ActiveStep: 0,
  TourDateDetails: [],
  TourAddonDetails: [],
  TourPlaceDetails: [],
  TourItineraryDetails: [],
  AddonDeleteIndex: -1,
  TourCostDetails: {
    ID: null,
    TourID: null,
    StartDate: "",
    EndDate: "",
    CurrencyID: null,
    RoomTypeID: null,
    VehicleSharingPaxID: null,
    RateType: null,
    RateNoOfNights: 0,
    PPRate: 0,
    ExtraAdultRate: 0,
    CWBRate: 0,
    CNBRate: 0,
    InfantRate: 0,
    FourSharingRate: 0,
    ThreeSharingRate: 0,
  },
  deleteIndex: -1,
  TourDetail: {
    SectorType: "",
    SectorID: null,
    SubSectorID: null,
    GSTID: "",
    TravelType: "",
    TourName: "",
    TourURL: "",
    SpecialityTypeID: [],
    TourCode: "",
    CompanyID: null,
    NoOfNights: null,
    NoOfDays: null,
    SpecialRemarks: "",
    PickupPlace: "",
    PickupTime: "",
    PickupRemarks: "",
    DropPlace: "",
    DropTime: "",
    DropRemarks: "",
    PickupFlightPlace: "",
    DropFlightPlace: "",
    SimilarPackage: "",
    IsActive: 1,
  },
  TourImageList: [],
  TourImageDetail: {
    attachment: [],
    ID: null,
    TourID: null,
    MainImage: "",
    BannerImage: "",
    Brochure: "",
  },
  TourCostAndAddonDetails: {},
};

const tourSlice = createSlice({
  name: "tour",
  initialState,
  reducers: {
    clearTourList: (state) => {
      state.TourList = initialState.TourList;
    },
    clearTourInformation: (state) => {
      state.TourDetail = initialState.TourDetail;
    },
    clearTourDateDetails: (state) => {
      state.TourDateDetails = initialState.TourDateDetails;
    },
    clearTourAddonDetails: (state) => {
      state.TourAddonDetails = initialState.TourAddonDetails;
    },
    clearTourItineraryDetails: (state) => {
      state.TourItineraryDetails = initialState.TourItineraryDetails;
    },
    clearTourPlaceDetails: (state) => {
      state.TourPlaceDetails = initialState.TourPlaceDetails;
    },
    setTourID: (state, action) => {
      state.TourID = action.payload;
    },
    clearTourID: (state) => {
      state.TourID = initialState.TourID;
    },
    setTourImageID: (state, action) => {
      state.TourImageID = action.payload;
    },
    clearTourImageID: (state) => {
      state.TourImageID = initialState.TourImageID;
    },
    setTourCostID: (state, action) => {
      state.TourCostID = action.payload;
    },
    clearTourCostID: (state) => {
      state.TourCostID = initialState.TourCostID;
    },
    setTourCostDetails: (state, action) => {
      state.TourCostDetails = action.payload;
    },
    clearTourCostDetails: (state) => {
      state.TourCostDetails = initialState.TourCostDetails;
    },
    setActiveStep: (state, action) => {
      state.ActiveStep = action.payload;
    },
    clearActiveStep: (state) => {
      state.ActiveStep = initialState.ActiveStep;
    },
    increaseActiveStep: (state) => {
      state.ActiveStep = state.ActiveStep + 1;
    },
    decreaseActiveStep: (state) => {
      state.ActiveStep = state.ActiveStep - 1;
    },
    setDeleteIndex: (state, action) => {
      state.deleteIndex = action.payload;
    },
    setAddonDeleteIndex: (state, action) => {
      state.AddonDeleteIndex = action.payload;
    },
    clearTourImageDetails: (state) => {
      state.TourImageDetail = initialState.TourImageDetail;
    },
    clearTourCostAndAddonDetails: (state) => {
      state.TourCostAndAddonDetails = initialState.TourCostAndAddonDetails;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllTours.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllTours.fulfilled,
      (state, action: PayloadAction<ITour[]>) => {
        state.loading = false;
        state.TourList = action.payload || [];
      },
    );
    builder.addCase(getAllTours.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.TourList = [];
    });
    builder.addCase(createTour.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(createTour.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createTour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(updateTour.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updateTour.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteTour.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteTour.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteTour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getTourByID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getTourByID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourDetail = action.payload;
    });
    builder.addCase(getTourByID.rejected, (state, action) => {
      state.loading = false;
      state.TourDetail = initialState.TourDetail;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getTourDatesByTourID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getTourDatesByTourID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourDateDetails = action.payload;
    });
    builder.addCase(getTourDatesByTourID.rejected, (state, action) => {
      state.loading = false;
      state.TourDateDetails = initialState.TourDateDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getTourItineraryByID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getTourItineraryByID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourItineraryDetails = action.payload;
    });
    builder.addCase(getTourItineraryByID.rejected, (state, action) => {
      state.loading = false;
      state.TourItineraryDetails = initialState.TourItineraryDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getTourPlacesByTourID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getTourPlacesByTourID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourPlaceDetails = action.payload;
    });
    builder.addCase(getTourPlacesByTourID.rejected, (state, action) => {
      state.loading = false;
      state.TourPlaceDetails = initialState.TourPlaceDetails;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getAllActiveTours.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveTours.fulfilled,
      (state, action: PayloadAction<ITour[]>) => {
        state.loading = false;
        state.TourList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveTours.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.TourList = [];
    });
    builder.addCase(ActiveInactiveTour.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveTour.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(ActiveInactiveTour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getTourRatesByTourID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getTourRatesByTourID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourCostList = action.payload;
    });
    builder.addCase(getTourRatesByTourID.rejected, (state, action) => {
      state.loading = false;
      state.TourCostList = initialState.TourCostList;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(insertTourRate.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(insertTourRate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(insertTourRate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(updateTourRate.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updateTourRate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTourRate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteTourRate.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteTourRate.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteTourRate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(updateTourOtherInfo.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updateTourOtherInfo.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTourOtherInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(TourImageUpload.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(TourImageUpload.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(TourImageUpload.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getImageByTourID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getImageByTourID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.TourImageDetail = action.payload;
    });
    builder.addCase(getImageByTourID.rejected, (state, action) => {
      state.loading = false;
      state.TourImageDetail = initialState.TourImageDetail;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteTourImage.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteTourImage.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteTourImage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getUpcomingTourDatesByTourID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getUpcomingTourDatesByTourID.fulfilled,
      (state, action: any) => {
        state.loading = false;
        state.TourDateDetails = action.payload;
      },
    );
    builder.addCase(getUpcomingTourDatesByTourID.rejected, (state, action) => {
      state.loading = false;
      state.TourDateDetails = initialState.TourDateDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(createTourAddons.pending, (state) => {
      state.AddonsLoading = true;
      state.error = "";
    });
    builder.addCase(createTourAddons.fulfilled, (state) => {
      state.AddonsLoading = false;
    });
    builder.addCase(createTourAddons.rejected, (state, action) => {
      state.AddonsLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getTourAddonsByTourID.pending, (state) => {
      state.AddonsLoading = true;
      state.error = "";
    });
    builder.addCase(getTourAddonsByTourID.fulfilled, (state, action: any) => {
      state.AddonsLoading = false;
      state.TourAddonDetails = action.payload;
    });
    builder.addCase(getTourAddonsByTourID.rejected, (state, action) => {
      state.AddonsLoading = false;
      state.TourAddonDetails = initialState.TourAddonDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(duplicateTour.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(duplicateTour.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(duplicateTour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getTourCostWithAddonsByID.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.TourCostAndAddonDetails = initialState.TourCostAndAddonDetails;
    });
    builder.addCase(
      getTourCostWithAddonsByID.fulfilled,
      (state, action: any) => {
        state.loading = false;
        state.TourCostAndAddonDetails = action.payload;
      },
    );
    builder.addCase(getTourCostWithAddonsByID.rejected, (state, action) => {
      state.loading = false;
      state.TourCostAndAddonDetails = initialState.TourCostAndAddonDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllGroupTours.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllGroupTours.fulfilled, (state, action) => {
      state.loading = false;
      state.GroupToursList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllGroupTours.rejected, (state, action) => {
      state.loading = false;
      state.GroupToursList = [];
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearTourList,
  clearTourInformation,
  clearTourDateDetails,
  clearTourAddonDetails,
  clearTourPlaceDetails,
  clearTourID,
  setTourID,
  setTourCostID,
  clearTourCostID,
  setTourCostDetails,
  clearTourCostDetails,
  setDeleteIndex,
  setAddonDeleteIndex,
  setActiveStep,
  clearActiveStep,
  increaseActiveStep,
  decreaseActiveStep,
  clearTourItineraryDetails,
  clearTourImageDetails,
  setTourImageID,
  clearTourImageID,
  clearTourCostAndAddonDetails,
} = tourSlice.actions;
export default tourSlice.reducer;
